/**
 * Period helpers for the SmartPeriodPill control.
 *
 * Period value model (the canonical shape stored in state):
 *   { type, offset?, days?, start?, end? }
 *
 *   type: "weekly" | "monthly" | "yearly" | "all"
 *       | "day"          // single day, uses offset (0=today, -1=yesterday)
 *       | "rolling"      // last N days from today, uses `days`
 *       | "custom"       // explicit start/end YYYY-MM-DD
 *
 *   offset: integer, only for weekly/monthly/yearly/day. 0 = current,
 *           -1 = previous. (Future offsets are clamped to 0.)
 *   days:   only for rolling. 7 | 30 | 90 (any positive integer works)
 *   start, end: "YYYY-MM-DD", only for custom.
 *
 * Backwards compatibility: a bare string "weekly"/"monthly"/"all"/
 * "alltime" is normalized to { type, offset: 0 }. null/undefined
 * defaults to weekly current.
 */

import { getWeekStart, todayStr, parseUTCDate, formatDateKey, resolveLocale } from "../utils";

/** Normalize any legacy or shorthand period value into the canonical object form. */
/**
 * @param {*} value
 * @param {boolean} [allowFuture=false] - calendar consumers pass true so a
 *   positive (future) offset survives normalization. Stats/leaderboard
 *   keep the default and future offsets are clamped to 0.
 */
export function normalizePeriod(value, allowFuture = false) {
  if (!value) return { type: "weekly", offset: 0 };
  if (typeof value === "string") {
    if (value === "alltime") return { type: "all" };
    if (value === "weekly" || value === "monthly" || value === "yearly") return { type: value, offset: 0 };
    if (value === "all") return { type: "all" };
    return { type: "weekly", offset: 0 };
  }
  if (typeof value === "object") {
    const t = value.type || "weekly";
    if (t === "all") return { type: "all" };
    if (t === "custom") return { type: "custom", start: value.start, end: value.end };
    if (t === "rolling") return { type: "rolling", days: Math.max(1, value.days | 0) };
    // weekly/monthly/yearly/day — clamp future offsets unless opted in
    const raw = value.offset | 0;
    const off = allowFuture ? raw : Math.min(0, raw);
    return { type: t, offset: off };
  }
  return { type: "weekly", offset: 0 };
}

/**
 * Resolve a period value into a concrete date range.
 *
 * @param {*} value - period value (raw or normalized)
 * @param {string} [today] - today's date as "YYYY-MM-DD". Defaults to current.
 * @returns {{ from: string|null, to: string|null, type: string }}
 *   from/to are inclusive "YYYY-MM-DD" strings, or null if unbounded ("all").
 */
export function resolvePeriod(value, today, allowFuture = false) {
  const p = normalizePeriod(value, allowFuture);
  const now = today || todayStr();
  if (p.type === "all") return { from: null, to: null, type: "all" };

  if (p.type === "weekly") {
    const ws = getWeekStart(now);
    if (p.offset === 0) {
      const we = addDays(ws, 6);
      return { from: ws, to: we, type: "weekly" };
    }
    const start = addDays(ws, p.offset * 7);
    const end = addDays(start, 6);
    return { from: start, to: end, type: "weekly" };
  }

  if (p.type === "monthly") {
    const [y, m] = now.split("-").map(Number);
    const d = new Date(Date.UTC(y, m - 1 + p.offset, 1));
    const start = formatDateKey(d);
    const endDate = new Date(Date.UTC(y, m + p.offset, 0));
    return { from: start, to: formatDateKey(endDate), type: "monthly" };
  }

  if (p.type === "yearly") {
    const [y] = now.split("-").map(Number);
    const yr = y + p.offset;
    return { from: `${yr}-01-01`, to: `${yr}-12-31`, type: "yearly" };
  }

  if (p.type === "day") {
    const ds = addDays(now, p.offset || 0);
    return { from: ds, to: ds, type: "day" };
  }

  if (p.type === "rolling") {
    const days = Math.max(1, p.days | 0);
    const start = addDays(now, -(days - 1));
    return { from: start, to: now, type: "rolling" };
  }

  if (p.type === "custom") {
    if (!p.start || !p.end) return { from: null, to: null, type: "custom" };
    const from = p.start <= p.end ? p.start : p.end;
    const to = p.start <= p.end ? p.end : p.start;
    return { from, to, type: "custom" };
  }

  return { from: null, to: null, type: "all" };
}

/** True if dateStr falls within the resolved period (or no filter, for "all"). */
export function inPeriod(dateStr, value, today) {
  if (!dateStr) return false;
  const r = resolvePeriod(value, today);
  if (r.from === null && r.to === null) return true;
  if (r.from && dateStr < r.from) return false;
  if (r.to && dateStr > r.to) return false;
  return true;
}

/**
 * Step the period by one unit (used by inline ‹/› navigation).
 * Returns null if the type doesn't support stepping.
 */
/**
 * Step a period forward/back. By default the future is clamped (stats /
 * leaderboard have no future data), but calendar-style consumers pass
 * `allowFuture` so the ‹/› arrows can navigate into upcoming months.
 */
export function stepPeriod(value, dir, allowFuture = false) {
  const p = normalizePeriod(value, allowFuture);
  if (p.type === "weekly" || p.type === "monthly" || p.type === "yearly" || p.type === "day") {
    const raw = (p.offset | 0) + dir;
    const next = allowFuture ? raw : Math.min(0, raw);
    return { ...p, offset: next };
  }
  if (p.type === "custom" && p.start && p.end) {
    // Shift custom range by its own length
    const len = daysBetween(p.start, p.end);
    const sign = dir < 0 ? -1 : 1;
    const start = addDays(p.start, sign * (len + 1));
    const end = addDays(p.end, sign * (len + 1));
    // Don't allow custom range to extend into the future (unless opted in)
    if (!allowFuture && sign > 0 && end > todayStr()) return null;
    return { type: "custom", start, end };
  }
  return null;
}

/** Whether the inline ‹/› arrows should be visible & enabled. */
export function canStep(value, dir, allowFuture = false) {
  return stepPeriod(value, dir, allowFuture) !== null;
}

/**
 * Whether stepping BACK one unit still lands on a period the viewer is
 * allowed to see. Used by `SmartPeriodPill`'s `minDate` prop (the viewer's
 * `historyFloor` in a gym) to disable the ‹ arrow once going further back
 * would only ever show a period entirely before the floor.
 *
 * A period that only PARTIALLY precedes `minDate` still steps back fine —
 * the data layer clamps `from` to the floor when it actually queries
 * (`clampRange` in `historyFloor.js`); this just stops the UI from letting
 * the viewer navigate into a period with nothing to show at all.
 *
 * @param {*} value - current period value (raw or normalized)
 * @param {string|null|undefined} minDate - "YYYY-MM-DD" floor, or falsy for none
 * @param {string} [today]
 * @returns {boolean}
 */
export function canStepBack(value, minDate, today) {
  if (!minDate) return true;
  const stepped = stepPeriod(value, -1);
  if (!stepped) return true; // not a steppable type — canStep already governs visibility
  const r = resolvePeriod(stepped, today);
  if (r.to == null) return true; // unbounded — shouldn't happen for a steppable type
  return r.to >= minDate;
}

/**
 * True when a period lies ENTIRELY before `minDate` — the viewer's history
 * floor (utils/historyFloor.js) — so it would show nothing. A period that only
 * starts before the floor is not "before" it: its range is clamped instead.
 */
export function isPeriodBeforeFloor(value, minDate, today, allowFuture = false) {
  if (!minDate) return false;
  const r = resolvePeriod(value, today, allowFuture);
  return r.to != null && r.to < minDate;
}

/**
 * Bring a (possibly persisted) period back inside the viewer's floor. Returns
 * the SAME reference when nothing needs to change, so a caller can compare
 * with `!==` and only then write it back.
 *
 * - A steppable period wholly before the floor → the current period of the
 *   same type ("last year" for someone who joined this year → "this year").
 * - A custom range wholly before the floor → "all" (= since you joined), or the
 *   current week when the control hides "all".
 * - A custom range that starts before the floor → its start moved to the floor.
 */
export function clampPeriodToFloor(value, minDate, today, { allowFuture = false, hideAll = false } = {}) {
  if (!minDate) return value;
  const p = normalizePeriod(value, allowFuture);
  if (isPeriodBeforeFloor(p, minDate, today, allowFuture)) {
    if (p.type === "weekly" || p.type === "monthly" || p.type === "yearly" || p.type === "day") {
      return { type: p.type, offset: 0 };
    }
    return hideAll ? { type: "weekly", offset: 0 } : { type: "all" };
  }
  if (p.type === "custom" && p.start && p.start < minDate) {
    return { type: "custom", start: minDate, end: p.end };
  }
  return value;
}

/**
 * Resolve a period into a current range plus the previous comparable range,
 * for "this period vs. previous period" headline comparisons (admin stats).
 *
 * - The current range never extends past `today` — an in-progress week /
 *   month / year only counts up to today (its `to` is clamped).
 * - The previous range is the same period stepped back one unit
 *   (`stepPeriod(-1)`): last week / month / year / day, or the shifted block
 *   for a custom range. For `rolling` (and any other non-stepping bounded
 *   type) it's the equal-length block ending the day before the current
 *   range starts.
 * - When the current period is still IN PROGRESS (its natural end is in the
 *   future), the previous range is TRUNCATED to the same elapsed length, so
 *   "this week so far" compares against "last week up to the same weekday"
 *   instead of a full prior week — otherwise every Monday looks catastrophic.
 *   (Same partial-window logic the TV `TvWeeklyComparison` slide uses.)
 *
 * @param {*} value - period value (raw or normalized)
 * @param {string} [today] - "YYYY-MM-DD" override (gym-timezone today)
 * @returns {{ cur: {from: string|null, to: string|null}, prev: {from: string, to: string}|null }}
 *   prev is null for "all" / unbounded periods (no baseline to compare to).
 */
export function comparePeriods(value, today) {
  const now = today || todayStr();
  const r = resolvePeriod(value, now);
  // Unbounded ("all"): nothing to compare against.
  if (r.from === null) return { cur: { from: null, to: null }, prev: null };

  const inProgress = !!(r.to && r.to > now);
  const curFrom = r.from;
  const curTo = inProgress ? now : r.to;
  const elapsed = daysBetween(curFrom, curTo); // inclusive-day offset (0 = single day)

  const stepped = stepPeriod(value, -1);
  let prevFrom, prevTo;
  if (stepped) {
    const pr = resolvePeriod(stepped, now);
    prevFrom = pr.from;
    prevTo = pr.to;
  } else {
    // rolling / non-steppable bounded: previous equal-length block.
    prevTo = addDays(curFrom, -1);
    prevFrom = addDays(prevTo, -elapsed);
  }
  if (prevFrom == null) return { cur: { from: curFrom, to: curTo }, prev: null };
  if (inProgress) prevTo = addDays(prevFrom, elapsed);

  return { cur: { from: curFrom, to: curTo }, prev: { from: prevFrom, to: prevTo } };
}

/**
 * Format a period for display in the pill.
 *
 * @param {*} value
 * @param {object} t - translation function from useTranslation()
 * @param {string} [lang] - "hu" | "en", for date formatting
 * @param {string} [today]
 * @returns {{ label: string, sub: string|null, isCurrent: boolean }}
 *   label: primary text ("Heti", "Havi", "Múlt hét", "Utolsó 30 nap", ...)
 *   sub:   secondary text ("máj 4 – máj 10", "Május 2026", "2026") — null for "all"
 *   isCurrent: true if this represents the present (offset === 0 etc.)
 */
export function formatPeriod(value, t, lang, today, allowFuture = false) {
  const p = normalizePeriod(value, allowFuture);
  const now = today || todayStr();

  if (p.type === "all") {
    return { label: t("period.type.all"), sub: null, isCurrent: true };
  }

  if (p.type === "weekly") {
    const r = resolvePeriod(p, now, allowFuture);
    const sub = formatDateRange(r.from, r.to, lang);
    if (p.offset === 0) return { label: t("period.preset.thisWeek"), sub, isCurrent: true };
    if (p.offset === -1) return { label: t("period.preset.lastWeek"), sub, isCurrent: false };
    return { label: t("period.type.weekly"), sub, isCurrent: false };
  }

  if (p.type === "monthly") {
    const r = resolvePeriod(p, now, allowFuture);
    const sub = formatMonthYear(r.from, lang);
    if (p.offset === 0) return { label: t("period.preset.thisMonth"), sub, isCurrent: true };
    if (p.offset === -1) return { label: t("period.preset.lastMonth"), sub, isCurrent: false };
    return { label: t("period.type.monthly"), sub, isCurrent: false };
  }

  if (p.type === "yearly") {
    const r = resolvePeriod(p, now, allowFuture);
    const yr = r.from?.substring(0, 4) || "";
    if (p.offset === 0) return { label: t("period.preset.thisYear"), sub: yr, isCurrent: true };
    if (p.offset === -1) return { label: t("period.preset.lastYear"), sub: yr, isCurrent: false };
    return { label: t("period.type.yearly"), sub: yr, isCurrent: false };
  }

  if (p.type === "day") {
    const r = resolvePeriod(p, now, allowFuture);
    if (p.offset === 0) return { label: t("period.preset.today"), sub: formatSingleDate(r.from, lang), isCurrent: true };
    if (p.offset === -1) return { label: t("period.preset.yesterday"), sub: formatSingleDate(r.from, lang), isCurrent: false };
    return { label: formatSingleDate(r.from, lang), sub: null, isCurrent: false };
  }

  if (p.type === "rolling") {
    const r = resolvePeriod(p, now, allowFuture);
    const lblKey = p.days === 7 ? "period.preset.last7"
      : p.days === 30 ? "period.preset.last30"
        : p.days === 90 ? "period.preset.last90"
          : null;
    const label = lblKey ? t(lblKey) : t("period.preset.lastNDays", { n: p.days });
    return { label, sub: formatDateRange(r.from, r.to, lang), isCurrent: true };
  }

  if (p.type === "custom") {
    const r = resolvePeriod(p, now, allowFuture);
    if (!r.from || !r.to) return { label: t("period.type.custom"), sub: null, isCurrent: false };
    return { label: formatDateRange(r.from, r.to, lang), sub: null, isCurrent: false };
  }

  return { label: "—", sub: null, isCurrent: false };
}

// ─── small date utilities (avoid pulling in moment / date-fns) ───

/** Add `n` days to a "YYYY-MM-DD" string and return new "YYYY-MM-DD". */
export function addDays(ds, n) {
  const d = parseUTCDate(ds);
  d.setUTCDate(d.getUTCDate() + n);
  return formatDateKey(d);
}

/** Inclusive day count between two "YYYY-MM-DD" strings (start ≤ end). */
export function daysBetween(start, end) {
  const a = parseUTCDate(start);
  const b = parseUTCDate(end);
  return Math.round((b - a) / 86400000);
}

/** Format a date range as "máj 4 – máj 10" / "May 4 – May 10". Same month gets compacted. */
export function formatDateRange(fromStr, toStr, lang) {
  if (!fromStr || !toStr) return "";
  const a = parseUTCDate(fromStr);
  const b = parseUTCDate(toStr);
  const locale = resolveLocale(lang);
  if (fromStr === toStr) return formatSingleDate(fromStr, lang);
  const sameYear = a.getUTCFullYear() === b.getUTCFullYear();
  const sameMonth = sameYear && a.getUTCMonth() === b.getUTCMonth();
  if (sameMonth) {
    if (lang === "hu") {
      // "máj 4–10" (HU)
      const m = a.toLocaleDateString(locale, { month: "short", timeZone: "UTC" });
      return `${m} ${a.getUTCDate()}–${b.getUTCDate()}.`;
    }
    // "May 4–10" (EN)
    const m = a.toLocaleDateString(locale, { month: "short", timeZone: "UTC" });
    return `${m} ${a.getUTCDate()}–${b.getUTCDate()}`;
  }
  const af = a.toLocaleDateString(locale, { month: "short", day: "numeric", timeZone: "UTC" });
  const bf = b.toLocaleDateString(locale, { month: "short", day: "numeric", timeZone: "UTC" });
  if (sameYear) return `${af} – ${bf}`;
  return `${a.getUTCFullYear()}. ${af} – ${b.getUTCFullYear()}. ${bf}`;
}

/** Format a single date — "máj 4." / "May 4". */
export function formatSingleDate(ds, lang) {
  if (!ds) return "";
  const d = parseUTCDate(ds);
  const locale = resolveLocale(lang);
  return d.toLocaleDateString(locale, { month: "short", day: "numeric", timeZone: "UTC" });
}

/** Format a YYYY-MM-DD as "Május 2026" / "May 2026". */
export function formatMonthYear(ds, lang) {
  if (!ds) return "";
  const d = parseUTCDate(ds);
  const locale = resolveLocale(lang);
  return d.toLocaleDateString(locale, { month: "long", year: "numeric", timeZone: "UTC" });
}
